<script setup lang="ts">
import lodashIsNumber from 'lodash-es/isNumber';

import { AuctionState, DirectSaleComputedState, type SaleAssetBusListingItem, SellingMethod } from '~/apiClient';
import { ROUTE_NAMES } from '~/constants/routes';
import { isAuction, isDirectSale, type PlatformSaleListingItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale: PlatformSaleListingItemEntity;
}

const { sale } = defineProps<Props>();

// EVENTS
const emit = defineEmits<{
    navigate: [];
}>();

// COMPOSABLE
const { tt, td, tn, formatUnits } = useTypedI18n();
const { auctionDetail, directSaleDetail } = useA11y();
const { isBundle } = useSaleType(() => sale.type);
const { getPlatformBusClassesLabel } = usePlatformBusClass();

// DATA
const mainAsset = computed<SaleAssetBusListingItem>(() => sale.mainAsset);

const classLabel = computed<string | undefined>(() => {
    const parts = [
        isBundle.value && sale?.assetCount ? tt('asset.vehicleCount', { count: sale?.assetCount }) : null,
        mainAsset.value.class ? getPlatformBusClassesLabel(mainAsset.value.class) : null,
    ].filter(Boolean);

    return parts.length ? parts.join(', ') : undefined;
});

const auctionBidInfo = computed(() => (isAuction(sale) && sale.state === AuctionState.Running ? sale.bidInfo : undefined));

const firstRegistration = computed(() => Date.parse(mainAsset.value.firstRegistration));

const priceColorClass = computed(() => {
    if (isAuction(sale)) {
        return auctionBidInfo.value && (auctionBidInfo.value.maxBidder ? 'text-primary' : 'text-warning-600');
    } else if (isDirectSale(sale)) {
        return sale.computedState === DirectSaleComputedState.NoActiveOrder ? 'text-primary' : 'text-default';
    }
});

const isHighlighted = computed(() => isAuction(sale) && (!sale.reservePriceSet || !!sale.reservePriceReached));

const linkProps = computed(() => {
    switch (sale.sellingMethod) {
        case SellingMethod.Auction:
            return { to: { name: ROUTE_NAMES.AUCTION_DETAIL, params: { slug: sale.slug } }, 'aria-label': auctionDetail(sale) };
        case SellingMethod.DirectSale:
            return { to: { name: ROUTE_NAMES.DIRECT_SALE_DETAIL, params: { slug: sale.slug } }, 'aria-label': directSaleDetail(sale) };
    }
});
</script>

<template>
    <NuxtLinkLocale v-bind="linkProps" @click="emit('navigate')">
        <SlpBaseCard :highlighted="isHighlighted">
            <template #header>
                <SlpCardImages :main-asset="mainAsset" />
                <div class="absolute top-3 right-3 left-3 flex justify-between">
                    <div class="flex items-start gap-3">
                        <SaleBadges :sale="sale" icon-only />
                    </div>
                    <SaleFavoritesButton :sale="sale" />
                </div>
                <AuctionReservePriceNotice v-if="isAuction(sale)" class="absolute right-3 bottom-3" :auction="sale" />
                <DslpCardNegotiationAvailableBadge v-if="isDirectSale(sale)" class="absolute right-3 bottom-3" :direct-sale="sale" />
            </template>

            <template #selling-method><SaleSellingMethod :selling-method="sale.sellingMethod" size="xs" /></template>
            <template v-if="classLabel" #class>{{ classLabel }}</template>

            <template #model>{{ mainAsset.brand.brandValue }} {{ mainAsset.model.modelValue }}</template>

            <template #registration>{{ td(firstRegistration, 'mmyyyyUTC') }}</template>

            <template #kilometer>{{ formatUnits.km(mainAsset.km) }}</template>

            <template #euro-norm>{{ mainAsset.euroNorm ? tt(`asset.euroNorm.${mainAsset.euroNorm}`) : '-' }}</template>

            <template #price>
                <template v-if="isAuction(sale) && lodashIsNumber(sale.currentPrice)">
                    <span :class="priceColorClass">
                        {{ tn(sale.currentPrice, 'currency') }}
                    </span>
                    <AuctionMaxBidderIcon :auction="sale" />
                </template>
                <span v-else-if="isDirectSale(sale)" :class="priceColorClass">
                    <template v-if="lodashIsNumber(sale.buyNowPrice)">
                        {{ tn(sale.buyNowPrice, 'currency') }}
                    </template>
                    <template v-else>
                        <span class="text-xl leading-9">
                            {{ sale.priceOnRequest ? tt('slp.DirectSale.priceOnRequest') : tt('slp.DirectSale.priceForGuests') }}
                        </span>
                    </template>
                </span>
                <template v-else> ******* </template>
            </template>

            <template #status>
                <AlpCardStatus v-if="isAuction(sale)" :auction="sale" />
                <DslpCardStatus v-else-if="isDirectSale(sale)" :direct-sale="sale" />
            </template>
        </SlpBaseCard>
    </NuxtLinkLocale>
</template>
